import { SORT_KIND } from '../../utils/tableSort';

/**
 * What every invoicing list column IS, so the shared sorter can order it
 * correctly (invoice-list-column-sorting, 2026-08-20).
 *
 * Declared once per list, in header order. Three columns do not sort the way
 * they read and are the reason this file exists:
 *   - Reporting Month renders "Jul 2026"; it sorts on the underlying date.
 *   - Status is a WORKFLOW; alphabetically APPROVED would precede DRAFT.
 *   - Payment / Applied / Converted are derived in the UI from other fields;
 *     they sort on the same derivation the cell renders.
 */

// Mirrors invoicing.models — the model's choices order IS the workflow order.
// Pinned by invoicing.tests_list_column_sorting so the two can't drift.
export const INVOICE_STATUS_ORDER = [
  'DRAFT', 'PENDING_APPROVAL', 'REJECTED', 'APPROVED',
  'PENDING_CONVERSION_APPROVAL', 'SENT', 'PAID', 'CANCELLED', 'OVERDUE', 'VOIDED',
];

export const PURCHASE_ORDER_STATUS_ORDER = [
  'DRAFT', 'PENDING_APPROVAL', 'REJECTED', 'APPROVED',
  'PENDING_CONVERSION_APPROVAL', 'SENT', 'CONFIRMED', 'PARTIALLY_RECEIVED',
  'FULLY_RECEIVED', 'CLOSED', 'CANCELLED',
];

// Severity, not alphabet: what needs chasing rises to the top. 'Scheduled'
// only occurs on the AP side (a payment run is booked) and sits between
// "nobody has acted" and "done".
export const PAYMENT_STATUS_ORDER = ['Overdue', 'Pending', 'Scheduled', 'Partially Paid', 'Paid'];
export const APPLIED_ORDER = ['Pending', 'Applied'];
export const CONVERTED_ORDER = ['Pending', 'Converted'];

/** AR payment state — the same derivation the Outgoing list's badge renders. */
/* AR payment state.
 *
 * `status === 'PAID'` is checked as well as the date, and it is not redundant.
 * Payments are now recorded as an event (date + amount + reference) and the
 * status follows, so the two agree on anything settled from here on. But rows
 * settled BEFORE that -- someone ticking PAID by hand, back when nothing wrote
 * the date field at all -- carry a status and no date, and used to render as
 * "Pending" right beside a "PAID" badge. Reading either source keeps those
 * honest without waiting on the backfill. */
/* Part-paid (payments ledger, Marcel 28.09): money arrived, but less than the
 * gross, so the rest is still open. Read from the amounts the list already
 * carries, so a part-paid invoice never reads "Paid" off its payment date. */
export const isPartPaid = (invoice) => {
  if (invoice.status === 'PAID') return false;
  const paid = parseFloat(invoice.payment_amount);
  const gross = parseFloat(invoice.gross_amount);
  return paid > 0 && gross > 0 && gross - paid > 0.01;
};

/** "Paid X of Y, open Z" for a part-paid invoice's badge. */
export const partPaidSummary = (invoice) => {
  const fmt = (n) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const paid = parseFloat(invoice.payment_amount) || 0;
  const gross = parseFloat(invoice.gross_amount) || 0;
  const cur = invoice.currency ? `${invoice.currency} ` : '';
  return `Paid ${cur}${fmt(paid)} of ${fmt(gross)}, open ${cur}${fmt(gross - paid)}`;
};

export const outgoingPaymentStatus = (invoice) => {
  if (isPartPaid(invoice)) return 'Partially Paid';
  if (invoice.payment_received_date || invoice.status === 'PAID') return 'Paid';
  if (invoice.is_overdue_display) return 'Overdue';
  return 'Pending';
};

/** AP payment state — the same derivation the Incoming list's badge renders. */
/* 'Scheduled' is checked BEFORE 'Overdue' (ledger row 120). A payment run sets
 * payment_scheduled_date, and a run pays what is due -- so nearly every invoice
 * on one is already past its due date. Checked after Overdue, the badge the run
 * exists to fill would almost never show; an invoice someone has already put on
 * a run is not one nobody has acted on. Cancelling the run clears the date, and
 * the invoice reads Overdue again. */
export const incomingPaymentStatus = (invoice) => {
  if (isPartPaid(invoice)) return 'Partially Paid';
  if (invoice.payment_executed_date || invoice.status === 'PAID') return 'Paid';
  if (invoice.payment_scheduled_date) return 'Scheduled';
  if (invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'PAID') {
    return 'Overdue';
  }
  return 'Pending';
};

const text = (key, label, value) => ({ key, label, kind: SORT_KIND.TEXT, value: value || ((r) => r[key]) });
const money = (key, label) => ({ key, label, kind: SORT_KIND.NUMBER, value: (r) => r[key] });
const date = (key, label) => ({ key, label, kind: SORT_KIND.DATE, value: (r) => r[key] });
const month = (key, label) => ({ key, label, kind: SORT_KIND.MONTH, value: (r) => r[key] });
const statusCol = (order = INVOICE_STATUS_ORDER) =>
  ({ key: 'status', label: 'Status', kind: SORT_KIND.ENUM, order, value: (r) => r.status });
const derived = (key, label, order, value) => ({ key, label, kind: SORT_KIND.ENUM, order, value });

const AMOUNT_COLUMNS = [
  money('net_amount', 'Net Amount'),
  money('vat_amount', 'VAT Amount'),
  money('gross_amount', 'Gross Amount'),
];

export const OUTGOING_INVOICE_COLUMNS = [
  text('invoice_number', 'Invoice Number'),
  text('source_proforma_number', 'Pro-forma Ref'),
  text('hms_entity_name', 'IMT Entity'),
  text('partner_name', 'Partner Name'),
  date('invoice_date', 'Invoice Date'),
  date('due_date', 'Due Date'),
  month('reporting_month', 'Reporting Month'),
  text('currency', 'Currency'),
  ...AMOUNT_COLUMNS,
  statusCol(),
  derived('payment_status', 'Payment Status', PAYMENT_STATUS_ORDER, outgoingPaymentStatus),
];

export const INCOMING_INVOICE_COLUMNS = [
  text('hms_expense_number', 'IMT Expense #'),
  text('supplier_invoice_number', 'Supplier Invoice #'),
  text('hms_entity_name', 'IMT Entity'),
  text('partner_name', 'Supplier Name'),
  date('invoice_date', 'Invoice Date'),
  date('due_date', 'Due Date'),
  month('reporting_month', 'Reporting Month'),
  text('currency', 'Currency'),
  ...AMOUNT_COLUMNS,
  statusCol(),
  derived('payment_status', 'Payment Status', PAYMENT_STATUS_ORDER, incomingPaymentStatus),
];

export const PROFORMA_INVOICE_COLUMNS = [
  text('invoice_number', 'Pro-Forma Invoice Number'),
  text('hms_entity_name', 'IMT Entity'),
  text('partner_name', 'Partner Name'),
  date('invoice_date', 'Invoice Date'),
  date('due_date', 'Due Date'),
  month('reporting_month', 'Reporting Month'),
  text('currency', 'Currency'),
  ...AMOUNT_COLUMNS,
  statusCol(),
  derived('converted', 'Converted', CONVERTED_ORDER,
    (r) => (r.converted_to_outgoing ? 'Converted' : 'Pending')),
];

export const PURCHASE_ORDER_COLUMNS = [
  text('po_number', 'PO Number'),
  text('hms_entity_name', 'IMT Entity'),
  text('supplier_name', 'Supplier Name'),
  date('po_date', 'PO Date'),
  date('expected_delivery_date', 'Expected Delivery'),
  text('currency', 'Currency'),
  ...AMOUNT_COLUMNS,
  statusCol(PURCHASE_ORDER_STATUS_ORDER),
];

export const CREDIT_NOTE_COLUMNS = [
  text('credit_note_number', 'Credit Note Number'),
  text('original_invoice_number', 'Original Invoice'),
  text('partner_name', 'Customer'),
  text('currency', 'Currency'),
  money('gross_amount', 'Gross Amount'),
  date('invoice_date', 'Date'),
  statusCol(),
  derived('applied', 'Applied', APPLIED_ORDER, (r) => (r.is_applied ? 'Applied' : 'Pending')),
];

export const DEBIT_NOTE_COLUMNS = [
  text('debit_note_number', 'Debit Note Number'),
  text('original_invoice_number', 'Original Invoice'),
  text('partner_name', 'Supplier'),
  text('currency', 'Currency'),
  money('gross_amount', 'Gross Amount'),
  date('invoice_date', 'Date'),
  statusCol(),
  derived('applied', 'Applied', APPLIED_ORDER,
    (r) => (r.reduction_applied_date ? 'Applied' : 'Pending')),
];
